import { CheckCircle2, CircleAlert, Info, TriangleAlert, X } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import {
  ToastContext,
  type ToastApi,
  type ToastOptions,
} from '@/components/ui/toast'

type ToastVariant = 'success' | 'error' | 'warning' | 'info'

interface ToastMessage extends ToastOptions {
  id: number
  message: string
  variant: ToastVariant
  exiting: boolean
}

const toastExitDuration = 360
const toastSlotCollapseDuration = 180
const toastRemovalDuration = toastExitDuration + toastSlotCollapseDuration

const toastStyles: Record<
  ToastVariant,
  { icon: ReactNode; border: string; iconColor: string; defaultTitle: string }
> = {
  success: {
    icon: <CheckCircle2 className="size-5" />,
    border: 'border-status-success/35',
    iconColor: 'text-status-success',
    defaultTitle: 'Success',
  },
  error: {
    icon: <CircleAlert className="size-5" />,
    border: 'border-status-failed/40',
    iconColor: 'text-status-failed',
    defaultTitle: 'Something went wrong',
  },
  warning: {
    icon: <TriangleAlert className="size-5" />,
    border: 'border-status-partial/40',
    iconColor: 'text-status-partial',
    defaultTitle: 'Action required',
  },
  info: {
    icon: <Info className="size-5" />,
    border: 'border-status-assessed/35',
    iconColor: 'text-status-assessed',
    defaultTitle: 'Information',
  },
}

export function ToastProvider({ children }: PropsWithChildren) {
  const nextId = useRef(0)
  const dismissTimers = useRef(new Map<number, number>())
  const removalTimers = useRef(new Map<number, number>())
  const [messages, setMessages] = useState<ToastMessage[]>([])

  const dismiss = useCallback((id: number) => {
    const timer = dismissTimers.current.get(id)
    if (timer !== undefined) window.clearTimeout(timer)
    dismissTimers.current.delete(id)

    setMessages((current) =>
      current.map((message) =>
        message.id === id ? { ...message, exiting: true } : message,
      ),
    )

    if (removalTimers.current.has(id)) return
    const removalTimer = window.setTimeout(() => {
      removalTimers.current.delete(id)
      setMessages((current) => current.filter((message) => message.id !== id))
    }, toastRemovalDuration)
    removalTimers.current.set(id, removalTimer)
  }, [])

  useEffect(
    () => () => {
      dismissTimers.current.forEach((timer) => window.clearTimeout(timer))
      dismissTimers.current.clear()
      removalTimers.current.forEach((timer) => window.clearTimeout(timer))
      removalTimers.current.clear()
    },
    [],
  )

  const show = useCallback(
    (variant: ToastVariant, message: string, options: ToastOptions = {}) => {
      const id = ++nextId.current
      setMessages((current) => [
        ...current,
        { id, message, variant, exiting: false, ...options },
      ])
      const timer = window.setTimeout(
        () => dismiss(id),
        options.duration ?? 4500,
      )
      dismissTimers.current.set(id, timer)
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, options) => show('success', message, options),
      error: (message, options) => show('error', message, options),
      warning: (message, options) => show('warning', message, options),
      info: (message, options) => show('info', message, options),
    }),
    [show],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-label="Notifications"
        className="pointer-events-none fixed inset-x-4 top-4 z-120 flex flex-col items-end sm:left-auto sm:w-96"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`toast-slot w-full ${message.exiting ? 'toast-slot-exit' : ''}`}
          >
            <div className="min-h-0">
              <ToastItem
                toast={message}
                onDismiss={() => dismiss(message.id)}
              />
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastMessage
  onDismiss: () => void
}) {
  const styles = toastStyles[toast.variant]

  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className={`${toast.exiting ? 'toast-exit pointer-events-none' : 'toast-enter pointer-events-auto'} flex w-full items-start gap-3 rounded-xl border bg-surface p-4 shadow-sm ${styles.border}`}
    >
      <span className={`mt-0.5 shrink-0 ${styles.iconColor}`}>
        {styles.icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-primary">
          {toast.title || styles.defaultTitle}
        </p>
        <p className="mt-1 text-sm leading-5 text-secondary">{toast.message}</p>
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={onDismiss}
        className="grid size-8 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-inset hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-border"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}
